import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuChevronRight, LuTriangleAlert } from 'react-icons/lu';
import { fetchAccounts, searchMovements } from '../../api/endpointsBackend';
import { useFilters } from '../../contexts/FilterContext';
import { ConvertedAmountsProvider } from '../../contexts/ConvertedAmountsContext';
import { DATE_MODES } from '../../utils/dateUtils';
import { formatPhoneBalance } from '../../utils/formatters';
import usePhoneHomeCurrency from '../../hooks/usePhoneHomeCurrency';
import useOpenAccountMovements from '../../hooks/useOpenAccountMovements';
import { monthShort } from '../../utils/intlFormatters';
import { PREF_TO_FLAG, WAITING_FLAG } from '../../utils/movementAlerts';
import { usePhoneSheet } from './PhoneSheet';
import { usePhoneShell } from './PhoneShellContext';
import PhoneMovementRow from './PhoneMovementRow';
import { DesktopNoteCard } from './PhoneReadOnlyParts';

// Every review flag, as the desktop account row sends them. Taken from the
// shared map, because the filter validator rejects the whole request on an
// unknown value.
const REVIEW_FLAGS = [...Object.values(PREF_TO_FLAG), WAITING_FLAG];

// Days of movements per page of an account's list.
const PAGE_DAYS = 15;

/** The short month a reconciliation gap belongs to: its period's end, else its date. */
export function gapMonth(gap) {
  const date = gap?.period_end || gap?.as_of_date;
  if (!date) return '';
  return monthShort(Number(String(date).slice(5, 7)) - 1);
}

/**
 * The gap as the account row states it: the two magnitudes the user can
 * compare with the bank, and their difference.
 */
export function gapFigures(gap) {
  const recorded = Math.abs(gap?.derived_balance || 0);
  const bank = Math.abs(gap?.bank_balance || 0);
  return { recorded, bank, diff: Math.abs(recorded - bank) };
}

/**
 * "⚠ Review N movements ›": the account's movements that need review, on
 * Movements. The sheet closes first, so its pending back step cannot undo
 * the navigation or the filters.
 */
function ReviewButton({ account, count }) {
  const { t } = useTranslation('accounts');
  const navigate = useNavigate();
  const { close } = usePhoneSheet();
  const { clearFilters, addFilter, setDateMode } = useFilters();

  const openReview = async () => {
    await close();
    clearFilters();
    // The count spans all time, not the current window.
    setDateMode(DATE_MODES.ALL);
    addFilter({ entity: 'Movement', column: 'account_id', operator: 'in', values: [account.id] });
    addFilter({ entity: 'Movement', column: 'alert', operator: 'in', values: REVIEW_FLAGS });
    navigate('/movements');
  };

  return (
    <Flex
      as="button"
      type="button"
      onClick={openReview}
      align="center"
      gap="10px"
      w="100%"
      minH="52px"
      px="14px"
      borderRadius="14px"
      bg="phone.alertBg"
      color="phone.alert"
      textAlign="left"
      _active={{ bg: 'phone.alertBgActive' }}
    >
      <Box as={LuTriangleAlert} boxSize="18px" flexShrink={0} />
      <Text as="span" textStyle="phoneRowName" flex="1" minW={0}>
        {t(count === 1 ? 'phone.reviewOne' : 'phone.reviewOther', { count })}
      </Text>
      <Box as={LuChevronRight} boxSize="18px" flexShrink={0} />
    </Flex>
  );
}

/** What the reconciliation found, one line per statement that does not tie out. */
function Reconciliation({ reconciliation }) {
  const { t } = useTranslation('accounts');
  if (reconciliation?.status === 'matches') {
    return <Text textStyle="phoneMeta" color="phone.income">{t('phone.matches')}</Text>;
  }
  if (reconciliation?.status !== 'off') return null;
  const gaps = reconciliation.gaps || [];
  return (
    <Box>
      {gaps.map((gap, i) => {
        const { recorded, bank, diff } = gapFigures(gap);
        return (
          <Text key={i} textStyle="phoneMeta" color="phone.expense">
            {t('phone.reconcileLine', {
              month: gapMonth(gap),
              // The three figures share the account's currency, so none
              // carries a code.
              recorded: formatPhoneBalance(null, recorded),
              bank: formatPhoneBalance(null, bank),
              diff: formatPhoneBalance(null, diff),
            })}
          </Text>
        );
      })}
    </Box>
  );
}

/**
 * The account sheet's phone-only pieces, under the chart: the review button
 * and the reconciliation. The panel's own account fetch carries no
 * reconciliation, so the Accounts list passes in what it holds; a caller
 * without it (Home) leaves it out and this loads it. Either way it reloads
 * after a fix on the phone, so the count and the gaps stay current.
 */
export function PhoneAccountSummary({ account, accountId, reconciliation }) {
  const { refreshSignal } = usePhoneShell();
  const [fetched, setFetched] = useState(null);
  // The first run is skipped when the caller already passed the status.
  const seeded = useRef(reconciliation !== undefined);

  useEffect(() => {
    if (seeded.current) {
      seeded.current = false;
      return undefined;
    }
    let active = true;
    fetchAccounts(null, { includeReconciliation: true })
      .then((list) => {
        const found = (list || []).find((a) => a.id === accountId);
        if (active && found) {
          setFetched({
            accountId,
            reconciliation: found.reconciliation,
            needsReview: found.needs_review_count,
          });
        }
      })
      .catch(() => { /* the figures the sheet opened with stay */ });
    return () => { active = false; };
  }, [accountId, refreshSignal]);

  const current = fetched?.accountId === accountId ? fetched : null;
  const status = current ? current.reconciliation : reconciliation;
  const count = current ? current.needsReview : account?.needs_review_count;
  const hasReview = count > 0;
  const hasReconciliation = ['matches', 'off'].includes(status?.status);
  if (!hasReview && !hasReconciliation) return null;
  return (
    <Flex direction="column" gap="10px" px="16px" pb="12px">
      {hasReview && <ReviewButton account={account} count={count} />}
      {hasReconciliation && <Reconciliation reconciliation={status} />}
    </Flex>
  );
}

/**
 * An account's movements as phone rows, newest first, a page of days at a
 * time. A row opens the movement sheet over the account sheet.
 */
export function PhoneAccountMovements({ account }) {
  const { t } = useTranslation('accounts');
  const accountId = account?.id;
  const { openMovement, refreshSignal } = usePhoneShell();
  const { close } = usePhoneSheet();
  const openAccountMovements = useOpenAccountMovements();
  // Rows in the home currency drop its code, as on Home and Movements.
  const home = usePhoneHomeCurrency();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async (nextPage) => {
    setLoading(true);
    setError(false);
    try {
      const res = await searchMovements(
        [{ entity: 'Movement', column: 'account_id', operator: 'in', values: [accountId] }],
        [{ entity: 'Movement', column: 'date', sort: 'desc' }],
        null, null, PAGE_DAYS, nextPage,
      );
      setRows((prev) => (nextPage === 0 ? res?.items || [] : [...prev, ...(res?.items || [])]));
      setPage(nextPage);
      setTotalPages(res?.total_pages || 0);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  // A save or fix anywhere on the phone reloads the first page.
  useEffect(() => { load(0); }, [load, refreshSignal]);

  return (
    <Box pb="16px">
      <Flex align="center" justify="space-between" gap="12px" px="16px" pt="8px" pb="4px" minH="44px">
        <Text textStyle="phoneCaption" color="phone.muted">{t('phone.movements')}</Text>
        {/* MI-345: the whole list on Movements. The sheet closes first, as
            the review button's does. */}
        {rows.length > 0 && (
          <Button
            variant="ghost"
            h="44px"
            px="8px"
            mr="-8px"
            color="phone.link"
            fontWeight="500"
            rightIcon={<LuChevronRight />}
            iconSpacing="2px"
            onClick={async () => { await close(); openAccountMovements(accountId); }}
          >
            {t('phone.seeAllMovements')}
          </Button>
        )}
      </Flex>
      <ConvertedAmountsProvider>
        {rows.map((m) => (
          <PhoneMovementRow key={m.id} movement={m} showDate homeCurrency={home} onClick={() => openMovement(m.id)} />
        ))}
      </ConvertedAmountsProvider>
      {loading && (
        <Flex justify="center" py="16px"><Spinner size="md" color="phone.muted" /></Flex>
      )}
      {!loading && error && (
        <Text textStyle="phoneMeta" color="phone.muted" px="16px" py="12px">{t('phone.loadFailed')}</Text>
      )}
      {!loading && !error && rows.length === 0 && (
        <Text textStyle="phoneMeta" color="phone.muted" px="16px" py="12px">{t('phone.noMovements')}</Text>
      )}
      {!loading && page + 1 < totalPages && (
        <Button
          variant="ghost"
          w="100%"
          h="48px"
          color="phone.link"
          fontWeight="500"
          onClick={() => load(page + 1)}
        >
          {t('phone.loadEarlier')}
        </Button>
      )}
      <DesktopNoteCard mt="16px" />
    </Box>
  );
}
